// src/pages/InsightsQueryPage.tsx

import React, { useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeftIcon, 
  LightBulbIcon, 
  DocumentTextIcon,
  ChartBarIcon,
  ClockIcon,
  UserIcon,
  ArrowTopRightOnSquareIcon as ExternalLink
} from '@heroicons/react/24/outline';
import { apiClient } from '../lib/apiClient';
import PipelineLoadingOverlay from '../components/PipelineLoadingOverlay';
import SlackIcon from '../components/icons/SlackIcon';
import GongIcon from '../components/icons/GongIcon';
import JiraIcon from '../components/icons/JiraIcon';
import MondayIcon from '../components/icons/MondayIcon';
import GoogleDriveIcon from '../components/icons/GoogleDriveIcon';
import { sendLocalDebugIngest } from '../lib/debugIngest';
import {
  isDigestResponse,
  isProgressResponse,
  isRiskResponse,
  isNarrativeResponse,
  isRAGResponse,
  WeeklyDigestResponse,
  ProgressSummaryResponse,
  RiskAssessmentResponse,
  AccountNarrativeResponse,
} from '../components/insights/types';
import WeeklyDigestView from '../components/insights/WeeklyDigestView';
import ProgressSummaryView from '../components/insights/ProgressSummaryView';
import RiskAssessmentView from '../components/insights/RiskAssessmentView';
import AccountNarrativeView from '../components/insights/AccountNarrativeView';

interface InsightResponse {
  query: string;
  customer_id?: string;
  customer_name?: string;
  context_type: string;
  response: {
    analysis: string;
    key_insights: string[];
    actionable_recommendations?: string[];
    strategic_recommendations?: string[];
    risk_assessment?: string;
    opportunities?: string;
  };
  confidence: number;
  sources?: Array<{
    type: string;
    channel: string;
    timestamp: string;
    relevance_score: number;
    user_name: string;
    user_email?: string;
    url?: string;
  }> | {
    local?: Array<{
      type: string;
      channel?: string;
      id?: string;
      key?: string;
      relevance_score: number;
    }>;
    web?: Array<{
      type: string;
      title: string;
      url: string;
      snippet: string;
    }>;
  };
  context_used: number;
  web_results_used?: number;
  local_data_used?: boolean;
  industry_context_used?: boolean;
  advisor_mode?: boolean;
  generated_at: string;
}

type UnifiedResponse = InsightResponse | WeeklyDigestResponse | ProgressSummaryResponse | RiskAssessmentResponse | AccountNarrativeResponse;

// Type guard functions — re-exported from shared types for local use
function isDigestResponseLocal(data: any): data is WeeklyDigestResponse {
  return isDigestResponse(data);
}

function isProgressResponseLocal(data: any): data is ProgressSummaryResponse {
  return isProgressResponse(data);
}

function isRAGResponseLocal(data: any): data is InsightResponse {
  return isRAGResponse(data) && !isRiskResponse(data) && !isNarrativeResponse(data);
}

const InsightsQueryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { queryId } = useParams();
  const queryClient = useQueryClient();
  const query = searchParams.get('q') || location.state?.submittedQuery || '';
  
  // Check if we have MCP response data from navigation state
  const mcpResponse = useMemo(() => {
    if (!location.state?.mcpResponse) {
      return undefined;
    }

    return {
      ...location.state.mcpResponse,
      query: query || location.state.mcpResponse.query,
    };
  }, [location.state, query]);
  const mcpMessage = location.state?.message;
  
  // Determine if this is a cached result or a new query
  const isCachedResult = !!queryId;
  
  // Track previous mcpResponse to detect changes
  const prevMcpResponseRef = useRef<any>(null);
  
  // Invalidate query when mcpResponse changes (new navigation with different data)
  useEffect(() => {
    if (mcpResponse) {
      const currentQuery = mcpResponse.query || mcpResponse.topic;
      const prevQuery = prevMcpResponseRef.current?.query || prevMcpResponseRef.current?.topic;
      
      // If the query changed, invalidate all mcp-response queries to force refetch
      if (currentQuery && currentQuery !== prevQuery) {
        queryClient.invalidateQueries({ queryKey: ['mcp-response'] });
      }
      
      prevMcpResponseRef.current = mcpResponse;
    }
  }, [mcpResponse, queryClient]);

  // Helper function to get source icon based on type
  const getSourceIcon = (sourceType: string) => {
    switch (sourceType.toLowerCase()) {
      case 'slack':
        return <SlackIcon size={16} className="mr-2" />;
      case 'gong':
        return <GongIcon size={16} className="mr-2" />;
      case 'jira':
        return <JiraIcon width={16} height={16} className="mr-2" />;
      case 'monday':
        return <MondayIcon className="w-4 h-4 mr-2" />;
      case 'google-drive':
      case 'googledrive':
      case 'drive':
        return <GoogleDriveIcon className="w-4 h-4 mr-2" />;
      default:
        return <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>;
    }
  };

  // API call using React Query
  const { data, isLoading, error, refetch } = useQuery<UnifiedResponse>({
    queryKey: mcpResponse ? ['mcp-response', query || mcpResponse.query || mcpResponse.topic || Date.now()] : (isCachedResult ? ['cached-query', queryId] : ['insights-query', query]),
    queryFn: async (): Promise<InsightResponse> => {
      // If we have MCP response data, use it directly
      if (mcpResponse) {
        return mcpResponse;
      }
      
      if (isCachedResult) {
        // Fetch cached result
        if (!queryId) throw new Error('No query ID provided');
        return await apiClient.customRequest<InsightResponse>(`/api/insights/cached/${queryId}`);
      } else {
        // Run new query
        if (!query) throw new Error('No query provided');
        return await apiClient.customRequest<InsightResponse>('/api/insights/query', {
          method: 'POST',
          body: {
            query: query,
            customer_id: null, // Let the backend handle customer resolution
            context_type: 'general'
          }
        });
      }
    },
    enabled: !!mcpResponse || (isCachedResult ? !!queryId : !!query),
    staleTime: mcpResponse ? Infinity : (isCachedResult ? Infinity : 0), // Use MCP data immediately, cache cached results forever, always fetch fresh for new queries
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const ragSources = isRAGResponseLocal(data) && Array.isArray((data as InsightResponse).sources)
      ? (data as InsightResponse).sources as Array<{ timestamp?: string; channel?: string }>
      : [];
    const sourceTimestamps = ragSources.slice(0, 5).map((source) => source.timestamp);
    const sourceChannels = ragSources.slice(0, 5).map((source) => source.channel);

    sendLocalDebugIngest({
      hypothesisId: 'H3,H4,H5',
      location: 'InsightsQueryPage.tsx:158',
      message: 'Insights page data snapshot',
      data: {
        urlQuery: query,
        mcpQuery: mcpResponse?.query,
        mcpTopic: mcpResponse?.topic,
        dataQuery: 'query' in data ? data.query : undefined,
        dataCustomerName: 'customer_name' in data ? data.customer_name : undefined,
        responseType: isDigestResponse(data) ? 'digest'
          : isProgressResponse(data) ? 'progress'
          : isRiskResponse(data) ? 'risk_assessment'
          : isNarrativeResponse(data) ? 'account_narrative'
          : isRAGResponseLocal(data) ? 'rag' : 'unknown',
        sourceTimestamps,
        sourceChannels,
        generatedAt: 'generated_at' in data ? data.generated_at : undefined,
      },
    });
  }, [data, mcpResponse, query]);

  useEffect(() => {
    if (!mcpResponse && !data && !query) {
      return;
    }
    sendLocalDebugIngest({
      hypothesisId: 'H2',
      location: 'InsightsQueryPage.tsx:382',
      message: 'Insights page query state',
      data: {
        searchParamQuery: query,
        mcpQuery: mcpResponse?.query,
        mcpTopic: mcpResponse?.topic,
        dataQuery: (data as any)?.query,
        dataCustomerName: (data as any)?.customer_name,
        isCachedResult,
        hasMcpResponse: !!mcpResponse,
      },
    });
  }, [data, isCachedResult, mcpResponse, query]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleNewQuery = () => {
    navigate('/');
  };

  if (!isCachedResult && !query && !mcpResponse) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">No Query Provided</h1>
          <p className="text-muted-foreground mb-6">Please provide a search query to get AI insights.</p>
          <button
            onClick={handleNewQuery}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (isCachedResult && !queryId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Invalid Query ID</h1>
          <p className="text-muted-foreground mb-6">The requested query result could not be found.</p>
          <button
            onClick={handleNewQuery}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackClick}
                className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back
              </button>
              <div>
                <h1 className="text-xl font-semibold text-foreground">EDYT Insights</h1>
                <p className="text-sm text-muted-foreground">
                  {isCachedResult ? 
                    `Cached Result: "${data?.query || 'Loading...'}"` : 
                    `Query: "${query || data?.query || 'Loading...'}"`}
                </p>
              </div>
            </div>
            <button
              onClick={handleNewQuery}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              New Query
            </button>
          </div>
        </div>
      </div>

      {/* MCP Message Display */}
      {mcpMessage && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mx-4 sm:mx-6 lg:mx-8 mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <LightBulbIcon className="h-5 w-5 text-blue-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {mcpMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pipeline Loading Overlay */}
      <PipelineLoadingOverlay
        isVisible={isLoading}
        requestType="rag_query"
        query={query}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {error && (
          <div className="bg-destructive/10 border border-destructive rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-destructive">Error</h3>
                <div className="mt-2 text-sm text-destructive">
                  <p>Failed to process your query. Please try again.</p>
                  <button
                    onClick={() => refetch()}
                    className="mt-2 text-destructive hover:text-destructive/80 underline"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Conditional Rendering Based on Response Type */}
            {isDigestResponse(data) && <WeeklyDigestView data={data} />}
            {isProgressResponse(data) && <ProgressSummaryView data={data} />}
            {isRiskResponse(data) && <RiskAssessmentView data={data} />}
            {isNarrativeResponse(data) && <AccountNarrativeView data={data} />}
            {isRAGResponseLocal(data) && (
              <>
            {/* Customer Context */}
            {data.customer_name && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Showing insights for: {data.customer_name}
                  </span>
                </div>
              </div>
            )}

            {/* Main Response */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground flex items-center">
                  <LightBulbIcon className="h-5 w-5 text-yellow-500 mr-2" />
                  AI Analysis
                </h2>
                <div className="flex items-center text-sm text-muted-foreground">
                  <ChartBarIcon className="h-4 w-4 mr-1" />
                  {data.confidence}% confidence
                </div>
              </div>
              
              <div className="prose max-w-none text-foreground leading-relaxed whitespace-pre-wrap">
                {data.response.analysis}
              </div>
            </div>

            {/* Key Insights Section */}
            {data.response.key_insights && data.response.key_insights.length > 0 && (
              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Key Insights</h3>
                <ul className="space-y-3 list-disc list-inside">
                  {data.response.key_insights.map((insight, index) => (
                    <li key={index} className="text-foreground">{insight}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Advisor Mode Badge */}
            {data.advisor_mode && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">PhD</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                      PhD-Level Advisor Analysis
                    </h3>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                      This response combines your local data with industry best practices and strategic insights.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Strategic Recommendations Section (Advisor Mode) */}
            {data.response.strategic_recommendations && data.response.strategic_recommendations.length > 0 && (
              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <LightBulbIcon className="h-5 w-5 text-purple-500 mr-2" />
                  Strategic Recommendations
                </h3>
                <ul className="space-y-3">
                  {data.response.strategic_recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="text-foreground">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Regular Recommendations Section */}
            {data.response.actionable_recommendations && data.response.actionable_recommendations.length > 0 && (
              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <LightBulbIcon className="h-5 w-5 text-green-500 mr-2" />
                  Actionable Recommendations
                </h3>
                <ul className="space-y-3">
                  {data.response.actionable_recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="text-foreground">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Risk Assessment Section (Advisor Mode) */}
            {data.response.risk_assessment && (
              <div className="bg-card rounded-lg shadow-sm border border-red-200 dark:border-red-800 p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <ChartBarIcon className="h-5 w-5 text-red-500 mr-2" />
                  Risk Assessment
                </h3>
                <div className="prose max-w-none text-foreground leading-relaxed whitespace-pre-wrap">
                  {data.response.risk_assessment}
                </div>
              </div>
            )}

            {/* Opportunities Section (Advisor Mode) */}
            {data.response.opportunities && (
              <div className="bg-card rounded-lg shadow-sm border border-green-200 dark:border-green-800 p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <LightBulbIcon className="h-5 w-5 text-green-500 mr-2" />
                  Opportunities
                </h3>
                <div className="prose max-w-none text-foreground leading-relaxed whitespace-pre-wrap">
                  {data.response.opportunities}
                </div>
              </div>
            )}

            {/* Sources - Handle both regular and advisor formats */}
            {data.sources && (Array.isArray(data.sources) ? data.sources.length > 0 : (data.sources.local && data.sources.local.length > 0) || (data.sources.web && data.sources.web.length > 0)) && (
              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-blue-500 mr-2" />
                  Sources
                  {data.advisor_mode && (
                    <span className="ml-2 text-sm text-muted-foreground">
                      ({data.context_used} local contexts, {data.web_results_used || 0} web sources)
                    </span>
                  )}
                  {!data.advisor_mode && (
                    <span className="ml-2 text-sm text-muted-foreground">
                      ({data.context_used} contexts used)
                    </span>
                  )}
                </h3>
                
                {/* Advisor Mode: Separate Local and Web Sources */}
                {data.advisor_mode && typeof data.sources === 'object' && 'local' in data.sources && (
                  <div className="space-y-6">
                    {/* Local Sources */}
                    {data.sources.local && data.sources.local.length > 0 && (
                      <div>
                        <h4 className="text-md font-semibold text-foreground mb-3 flex items-center">
                          <DocumentTextIcon className="h-4 w-4 text-blue-500 mr-2" />
                          Local Data Sources ({data.sources.local.length})
                        </h4>
                        <div className="space-y-2">
                          {data.sources.local.map((source, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
                              <div className="flex items-center flex-1">
                                {getSourceIcon(source.type)}
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-foreground">
                                      {source.channel || source.id || source.key || 'Unknown'}
                                    </p>
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                                      {source.type.toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-sm text-muted-foreground font-medium ml-4">
                                {Math.round(source.relevance_score * 100)}% relevant
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Web Sources */}
                    {data.sources.web && data.sources.web.length > 0 && (
                      <div>
                        <h4 className="text-md font-semibold text-foreground mb-3 flex items-center">
                          <ExternalLink className="h-4 w-4 text-green-500 mr-2" />
                          Industry Knowledge Sources ({data.sources.web.length})
                        </h4>
                        <div className="space-y-2">
                          {data.sources.web.map((source, index) => (
                            <div key={index} className="p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors">
                              <a 
                                href={source.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm font-semibold text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 hover:underline inline-flex items-center group mb-1"
                              >
                                {source.title}
                                <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                              </a>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {source.snippet}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Regular Mode: Array of Sources */}
                {!data.advisor_mode && Array.isArray(data.sources) && data.sources.length > 0 && (
                <div className="space-y-3">
                  {data.sources.map((source, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border hover:bg-muted transition-colors">
                      <div className="flex items-center flex-1">
                        {getSourceIcon(source.type)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            {source.url && source.url !== '#' ? (
                              <a 
                                href={source.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline inline-flex items-center group"
                              >
                                {source.channel}
                                <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                              </a>
                            ) : (
                              <p className="text-sm font-medium text-foreground">
                                {source.channel}
                              </p>
                            )}
                            <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                              {source.type.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            by <span className="font-semibold text-foreground bg-yellow-100 dark:bg-yellow-900/20 px-1.5 py-0.5 rounded">{source.user_name}</span>
                            {source.user_email && (
                              <span className="text-muted-foreground ml-1">({source.user_email})</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {source.timestamp ? new Date(parseFloat(source.timestamp) * 1000).toLocaleString() : 'No timestamp'}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground font-medium ml-4">
                        {Math.round(source.relevance_score * 100)}% relevant
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}

            {/* Metadata */}
            <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-muted-foreground space-y-2 sm:space-y-0">
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  Generated at {new Date(data.generated_at).toLocaleString()}
                </div>
                    <div className="flex items-center space-x-4">
                <div>
                  Context type: {data.context_type}
                </div>
                      {data.advisor_mode && (
                        <>
                          {data.local_data_used && (
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              Local data used
                            </div>
                          )}
                          {data.industry_context_used && (
                            <div className="flex items-center">
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Industry context used
                            </div>
                          )}
                        </>
                      )}
              </div>
            </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsQueryPage;
